import React, { useEffect, useState } from 'react';
import {
    Navigate,
    useLocation,
} from 'react-router-dom';

import { useAuth } from '../hooks/auth';

import Authorized from '../pages/Layouts/Authorized';
import NotAuthorized from '../pages/Layouts/NotAuthorized';
import { error_message } from '../components/Toast';

const getEmailLinkQueryParams = (search) => {
    const urlParamsObj = new URLSearchParams(search);
    return {
        companyID: urlParamsObj.get('companyID'),
        reportYearID: urlParamsObj.get('reportyearID'),
        selectedReportYear: urlParamsObj.get('selectedReportYear'),
        urlParamsObj,
    };
};

const hasEmailLinkQueryParams = ({ companyID, reportYearID }) => Boolean(companyID || reportYearID);

const isEmailLinkCompanyMismatch = (companyID, userData) =>
    companyID != null &&
    userData?.companyID != null &&
    String(userData.companyID) !== String(companyID);

const applyEmailLinkQueryParamsToStorage = (search) => {
    const params = getEmailLinkQueryParams(search);
    if (!hasEmailLinkQueryParams(params)) {
        return false;
    }

    const userData = JSON.parse(localStorage.getItem('@QualityApp:user') || '{}');
    if (isEmailLinkCompanyMismatch(params.companyID, userData)) {
        return false;
    }

    userData.selectedCompanyID = params.companyID;
    userData.selectedReportYearID = params.reportYearID;
    userData.selectedReportYear = params.selectedReportYear;
    localStorage.setItem('@QualityApp:user', JSON.stringify(userData));
    return true;
};

export const PrivateRoute = ({ children, allowedRoles }) => {
    const { user, userRoles, signOut } = useAuth();
    const location = useLocation();
    const [redirectTo, setRedirectTo] = useState('/');

    // Apply before child routes mount so Report reads the correct report year from storage.
    if (user && hasEmailLinkQueryParams(getEmailLinkQueryParams(location.search))) {
        applyEmailLinkQueryParamsToStorage(location.search);
    }

    useEffect(() => {
        if (user) {
            const { companyID, reportYearID, selectedReportYear, urlParamsObj } = getEmailLinkQueryParams(location.search);

            if (hasEmailLinkQueryParams({ companyID, reportYearID })) {
                const userData = JSON.parse(localStorage.getItem('@QualityApp:user') || '{}');

                if (isEmailLinkCompanyMismatch(companyID, userData)) {
                    error_message("You are logged in, but the supplier in the link doesn't match. Please log out and log in again.");
                    signOut();
                    return;
                }

                applyEmailLinkQueryParamsToStorage(location.search);

                urlParamsObj.delete('companyID');
                urlParamsObj.delete('reportyearID');
                urlParamsObj.delete('selectedReportYear');

                const cleanUrl = `${location.pathname}${urlParamsObj.toString() ? '?' + urlParamsObj.toString() : ''}`;
                window.history.replaceState({}, '', cleanUrl);

                localStorage.removeItem('@QualityApp:redirectAfterLogin');
                localStorage.removeItem('@QualityApp:companyID');
                localStorage.removeItem('@QualityApp:reportYearID');
                localStorage.removeItem("@QualityApp:selectedReportYear");
            }

            const savedRedirect = localStorage.getItem("@QualityApp:redirectAfterLogin");
            if (savedRedirect) {
                setRedirectTo(savedRedirect);
                
            } else {
                setRedirectTo('/');
            }
        }
    }, [user, userRoles, signOut, location.pathname, location.search]);

    if (user) {
        if (allowedRoles != null && !allowedRoles.some(role => userRoles?.includes(role))) {
            return <Navigate to="/" replace />;
        }
    }

    if (!user) {
        const currentPath = window.location.pathname;
        const urlParams = window.location.search;

        const urlParamsObj = new URLSearchParams(urlParams);
        const companyID = urlParamsObj.get('companyID');
        const reportYearID = urlParamsObj.get('reportyearID');
        const selectedReportYear = urlParamsObj.get('selectedReportYear');

        if (companyID && reportYearID) {
            localStorage.setItem("@QualityApp:redirectAfterLogin", currentPath);
            localStorage.setItem("@QualityApp:companyID", companyID);
            localStorage.setItem("@QualityApp:reportYearID", reportYearID);
            localStorage.setItem("@QualityApp:selectedReportYear", selectedReportYear);

        } else {
            //localStorage.setItem("@QualityApp:redirectAfterLogin", currentPath + urlParams);
        }
        return <Navigate to="/signin" replace />;
    }

    var roles = [];
    if (userRoles != null) {
        roles = userRoles.constructor === Array ? userRoles : JSON.parse(userRoles);
    }
    const Layout = user && roles.length > 0 ? Authorized : NotAuthorized;

    return (
        <Layout>
            {children}
        </Layout>
    );
};

export const PublicRoute = ({ children }) => {
    const { user, userRoles } = useAuth();

    var roles = [];
    if (userRoles != null) {
        roles = userRoles.constructor === Array ? userRoles : JSON.parse(userRoles);
    }
    const Layout = user && roles.length > 0 ? Authorized : NotAuthorized;

    return (
        <Layout>
            {children}
        </Layout>
    );
};
